import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  Stack,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import FormDialog from '../components/forms/FormDialog';
import AddAppointment from '../components/forms/AddAppointment';
import { fetchTokenAppointmentRequest, SelectedPatient } from 'src/modules/app/store/appActions';
import storage from 'src/utils/storageUtils';

const OPDQueueContainer = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatusTab, setSelectedStatusTab] = useState('ALL');
  const [openAddModal, setOpenAddModal] = useState(false);
  const [currentTime, setCurrentTime] = useState(dayjs().format('hh:mm:ss A'));

  // Action Menu state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeAppointment, setActiveAppointment] = useState(null);

  // Selected Clinic
  const selectedClinicStr = localStorage.getItem('selectedClinic');
  let selectedClinic = null;
  try {
    selectedClinic = selectedClinicStr ? JSON.parse(selectedClinicStr) : null;
  } catch (e) {
    selectedClinic = null;
  }

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(dayjs().format('hh:mm:ss A'));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const fetchData = async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    try {
      const token = storage.get('TOKEN');
      const response = await new Promise((resolve, reject) => {
        dispatch(fetchTokenAppointmentRequest(token, 1, 100, resolve, reject));
      });
      if (response) {
        const apptList = Array.isArray(response)
          ? response
          : Array.isArray(response.data)
            ? response.data
            : [];
        setAppointments(apptList);
      }
    } catch (err) {
      console.error('Failed to fetch OPD queue:', err);
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(false);

    const handleRealtimeUpdate = () => {
      fetchData(true);
    };

    window.addEventListener('appointmentCreated', handleRealtimeUpdate);
    window.addEventListener('appointmentUpdated', handleRealtimeUpdate);
    window.addEventListener('queueUpdated', handleRealtimeUpdate);
    window.addEventListener('notificationsUpdated', handleRealtimeUpdate);

    // Fast polling every 4 seconds for live queue monitoring
    const interval = setInterval(() => fetchData(true), 4000);

    return () => {
      window.removeEventListener('appointmentCreated', handleRealtimeUpdate);
      window.removeEventListener('appointmentUpdated', handleRealtimeUpdate);
      window.removeEventListener('queueUpdated', handleRealtimeUpdate);
      window.removeEventListener('notificationsUpdated', handleRealtimeUpdate);
      clearInterval(interval);
    };
  }, [dispatch]);

  // Navigate to Patient Profile
  const handleViewPatientProfile = (item) => {
    const patient = item?.patient;
    const pId = patient?.id || patient?._id || item?.patientId;
    const pName = patient?.fullName || patient?.name || '';
    if (pId) {
      storage.set('PATIENT_ID', pId);
      storage.set('PATIENT_NAME', pName);
      storage.set('SELECTED_PATIENT', JSON.stringify({ patientId: pId, patientName: pName, patient }));
      dispatch(SelectedPatient({ id: pId, patientId: pId, patientName: pName, patient }));
      navigate(`/viewprofile/${pId}`);
    }
    handleMenuClose();
  };

  // WhatsApp reminder
  const sendWhatsAppReminder = (item) => {
    const patientName = item.patient?.fullName || item.patient?.name || 'Patient';
    const rawPhone = item.patient?.contactNo || item.patient?.phone || '';
    const digits = String(rawPhone).replace(/\D/g, '');
    let phoneParam = '';
    if (digits.length >= 11) phoneParam = digits;
    else if (digits.length === 10) phoneParam = `91${digits}`;

    const tokenLabel = item.tokenNumber
      ? `#${item.tokenNumber}`
      : `APT-${(item.appointmentId || item.id || '').replace('APT-', '').slice(-5).toUpperCase()}`;

    const msg = `Hello ${patientName}, your token (${tokenLabel}) is now in line at ${selectedClinic?.clinicName || 'MyCliniq'}. Please be ready near the consultation room.`;
    window.open(`https://wa.me/${phoneParam}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const handleMenuOpen = (event, item) => {
    setMenuAnchor(event.currentTarget);
    setActiveAppointment(item);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveAppointment(null);
  };

  // Queue Statistics
  const stats = useMemo(() => {
    const total = appointments.length;
    const waiting = appointments.filter(
      (a) => a.recordStatus === 'In Queue' || a.recordStatus === 'Scheduled'
    ).length;
    const attending = appointments.filter((a) => a.recordStatus === 'Attending Now').length;
    const completed = appointments.filter((a) => a.recordStatus === 'Complete').length;
    return { total, waiting, attending, completed };
  }, [appointments]);

  // Filtered Appointments
  const filteredAppointments = useMemo(() => {
    return appointments.filter((item) => {
      const pName = item.patient?.fullName || item.patient?.name || '';
      const pPhone = item.patient?.contactNo || '';
      const apptId = item.appointmentId || item.id || '';
      const tokenNum = item.tokenNumber ? String(item.tokenNumber) : '';
      const matchesSearch =
        pName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pPhone.includes(searchQuery) ||
        apptId.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tokenNum.includes(searchQuery);

      if (!matchesSearch) return false;

      if (selectedStatusTab === 'ALL') return true;
      if (selectedStatusTab === 'WAITING')
        return item.recordStatus === 'In Queue' || item.recordStatus === 'Scheduled';
      if (selectedStatusTab === 'ATTENDING') return item.recordStatus === 'Attending Now';
      if (selectedStatusTab === 'COMPLETE') return item.recordStatus === 'Complete';
      if (selectedStatusTab === 'CANCELLED')
        return item.recordStatus === 'Cancelled' || item.recordStatus === 'Expired';
      return true;
    });
  }, [appointments, searchQuery, selectedStatusTab]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Attending Now':
        return { bg: '#E8F5E9', text: '#2E7D32', border: '#A5D6A7' };
      case 'In Queue':
      case 'Scheduled':
        return { bg: '#FFF8E1', text: '#F57F17', border: '#FFE082' };
      case 'Complete':
        return { bg: '#E3F2FD', text: '#1565C0', border: '#90CAF9' };
      case 'Cancelled':
      case 'Expired':
        return { bg: '#FFEBEE', text: '#C62828', border: '#FFCDD2' };
      default:
        return { bg: '#F5F5F5', text: '#616161', border: '#E0E0E0' };
    }
  };

  const isDark = theme.palette.mode === 'dark';

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {/* Top Banner & Clinic Header */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
              Live OPD Queue & Token Desk
            </Typography>
            <Chip
              label="LIVE"
              size="small"
              sx={{
                bgcolor: '#DC2626',
                color: '#FFFFFF',
                fontWeight: 700,
                fontSize: '11px',
                height: '22px',
                animation: 'pulse 1.5s infinite',
                '@keyframes pulse': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.6 },
                  '100%': { opacity: 1 },
                },
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
            {selectedClinic ? selectedClinic.clinicName : 'All Clinics'} • Live Queue Monitor •{' '}
            <strong style={{ color: theme.palette.text.primary }}>{currentTime}</strong>
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          <Tooltip title="Refresh Queue">
            <IconButton
              onClick={fetchData}
              disabled={loading}
              sx={{
                bgcolor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '10px',
                '&:hover': { bgcolor: theme.palette.action.hover },
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddModal(true)}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 1,
              bgcolor: theme.palette.primary.main,
              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
              '&:hover': { bgcolor: theme.palette.primary.dark },
            }}
          >
            Add to Queue / Token
          </Button>
        </Stack>
      </Box>

      {/* KPI Stats Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2.5,
          mb: 3.5,
        }}
      >
        {/* Total Queue Card */}
        <Card
          sx={{
            borderRadius: '16px',
            border: `1px solid ${isDark ? '#334155' : '#E2E8F0'}`,
            boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 2px 10px rgba(0,0,0,0.03)',
            background: isDark
              ? 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)'
              : 'linear-gradient(135deg, #FFFFFF 0%, #F8FAFC 100%)',
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', fontWeight: 600, textTransform: 'uppercase' }}>
                  Total Queue
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: theme.palette.text.primary, mt: 0.5 }}>
                  {stats.total}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: isDark ? 'rgba(79, 70, 229, 0.2)' : '#EEF2FF', color: '#818CF8', width: 48, height: 48 }}>
                <PeopleAltIcon />
              </Avatar>
            </Box>
            <Typography variant="caption" sx={{ color: isDark ? '#94A3B8' : '#64748B', display: 'block', mt: 1 }}>
              Today's registered tokens
            </Typography>
          </CardContent>
        </Card>

        {/* Waiting Card */}
        <Card
          sx={{
            borderRadius: '16px',
            border: `1px solid ${isDark ? '#78350F' : '#FEF3C7'}`,
            boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 2px 10px rgba(245, 158, 11, 0.08)',
            background: isDark
              ? 'linear-gradient(135deg, #2A1E0B 0%, #1E293B 100%)'
              : 'linear-gradient(135deg, #FFFFFF 0%, #FFFBEB 100%)',
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" sx={{ color: isDark ? '#FBBF24' : '#D97706', fontWeight: 600, textTransform: 'uppercase' }}>
                  Waiting in Queue
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: isDark ? '#FCD34D' : '#B45309', mt: 0.5 }}>
                  {stats.waiting}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: isDark ? 'rgba(217, 119, 6, 0.2)' : '#FEF3C7', color: '#FBBF24', width: 48, height: 48 }}>
                <AccessTimeIcon />
              </Avatar>
            </Box>
            <Typography variant="caption" sx={{ color: isDark ? '#FBBF24' : '#D97706', display: 'block', mt: 1 }}>
              Ready for consultation
            </Typography>
          </CardContent>
        </Card>

        {/* Attending Now Card */}
        <Card
          sx={{
            borderRadius: '16px',
            border: `1px solid ${isDark ? '#065F46' : '#DCFCE7'}`,
            boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 2px 10px rgba(34, 197, 94, 0.08)',
            background: isDark
              ? 'linear-gradient(135deg, #052E16 0%, #1E293B 100%)'
              : 'linear-gradient(135deg, #FFFFFF 0%, #F0FDF4 100%)',
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" sx={{ color: isDark ? '#4ADE80' : '#16A34A', fontWeight: 600, textTransform: 'uppercase' }}>
                  Attending Now
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: isDark ? '#86EFAC' : '#15803D', mt: 0.5 }}>
                  {stats.attending}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: isDark ? 'rgba(22, 163, 74, 0.2)' : '#DCFCE7', color: '#4ADE80', width: 48, height: 48 }}>
                <PlayCircleOutlineIcon />
              </Avatar>
            </Box>
            <Typography variant="caption" sx={{ color: isDark ? '#4ADE80' : '#16A34A', display: 'block', mt: 1 }}>
              Currently with Doctor
            </Typography>
          </CardContent>
        </Card>

        {/* Completed Card */}
        <Card
          sx={{
            borderRadius: '16px',
            border: `1px solid ${isDark ? '#0284C7' : '#E0F2FE'}`,
            boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 2px 10px rgba(2, 132, 199, 0.08)',
            background: isDark
              ? 'linear-gradient(135deg, #082F49 0%, #1E293B 100%)'
              : 'linear-gradient(135deg, #FFFFFF 0%, #F0F9FF 100%)',
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="caption" sx={{ color: isDark ? '#38BDF8' : '#0284C7', fontWeight: 600, textTransform: 'uppercase' }}>
                  Completed
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: isDark ? '#7DD3FC' : '#0369A1', mt: 0.5 }}>
                  {stats.completed}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: isDark ? 'rgba(2, 132, 199, 0.2)' : '#E0F2FE', color: '#38BDF8', width: 48, height: 48 }}>
                <CheckCircleIcon />
              </Avatar>
            </Box>
            <Typography variant="caption" sx={{ color: isDark ? '#38BDF8' : '#0284C7', display: 'block', mt: 1 }}>
              Consultations completed
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Main Queue Table Card */}
      <Card
        sx={{
          borderRadius: '16px',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 4px 16px rgba(0,0,0,0.03)',
          bgcolor: theme.palette.background.paper,
          overflow: 'hidden',
        }}
      >
        {/* Controls Bar: Tabs & Search */}
        <Box
          sx={{
            p: 2.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Tabs
            value={selectedStatusTab}
            onChange={(e, val) => setSelectedStatusTab(val)}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                minHeight: '40px',
                px: 2,
                borderRadius: '8px',
                color: theme.palette.text.secondary,
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            <Tab label={`All (${stats.total})`} value="ALL" />
            <Tab label={`Waiting (${stats.waiting})`} value="WAITING" />
            <Tab label={`Attending (${stats.attending})`} value="ATTENDING" />
            <Tab label={`Completed (${stats.completed})`} value="COMPLETE" />
            <Tab label="Cancelled" value="CANCELLED" />
          </Tabs>

          <TextField
            size="small"
            placeholder="Search patient, token, phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 280 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#F8FAFC',
              },
            }}
          />
        </Box>

        {/* Queue Items List / Table */}
        <Box sx={{ p: 2.5 }}>
          {filteredAppointments.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <LocalHospitalIcon sx={{ fontSize: 48, color: theme.palette.divider, mb: 1.5 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                No Patients in This Queue
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5, mb: 2 }}>
                Click below to add a walk-in patient or schedule a new appointment.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setOpenAddModal(true)}
                sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
              >
                Add New Patient to Queue
              </Button>
            </Box>
          ) : (
            <Stack spacing={2}>
              {filteredAppointments.map((item, index) => {
                const patientName = item.patient?.fullName || item.patient?.name || 'Walk-in Patient';
                const pAge = item.patient?.age ? `${item.patient.age} yrs` : '';
                const pGender = item.patient?.gender ? item.patient.gender : '';
                const pPhone = item.patient?.contactNo || '';
                
                const hasToken = Boolean(item.tokenNumber);
                const tokenDisplay = hasToken ? `#${item.tokenNumber}` : `APT-${(item.appointmentId || item.id || '').replace('APT-', '').slice(-5).toUpperCase()}`;
                const patientIdDisplay = `MYC-${(item.patient?.patientId || item.patient?.id || '').replace('MYC-', '').slice(-5).toUpperCase()}`;
                const apptIdDisplay = `APT-${(item.appointmentId || item.id || '').replace('APT-', '').slice(-5).toUpperCase()}`;

                const statusStyle = getStatusColor(item.recordStatus);

                const isAttending = item.recordStatus === 'Attending Now';
                const isWaiting = item.recordStatus === 'In Queue' || item.recordStatus === 'Scheduled';
                const isComplete = item.recordStatus === 'Complete';

                return (
                  <Card
                    key={item.id || index}
                    sx={{
                      borderRadius: '12px',
                      border: isAttending
                        ? '2px solid #22C55E'
                        : `1px solid ${theme.palette.divider}`,
                      bgcolor: isAttending
                        ? isDark ? 'rgba(34, 197, 94, 0.15)' : '#F0FDF4'
                        : isDark ? 'rgba(255, 255, 255, 0.03)' : '#FFFFFF',
                      boxShadow: isAttending ? '0 4px 12px rgba(34, 197, 94, 0.12)' : '0 2px 6px rgba(0,0,0,0.02)',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: isAttending ? '#22C55E' : theme.palette.primary.main,
                        boxShadow: isDark ? '0 6px 16px rgba(0,0,0,0.3)' : '0 6px 16px rgba(0,0,0,0.06)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        p: 2,
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                      }}
                    >
                      {/* Left: Token Number & Patient Info */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* Token Badge */}
                        <Box
                          sx={{
                            minWidth: 56,
                            height: 56,
                            px: 1,
                            borderRadius: '12px',
                            bgcolor: isAttending ? '#22C55E' : (hasToken ? theme.palette.primary.main : '#6366F1'),
                            color: '#FFFFFF',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 800,
                            fontSize: hasToken ? '16px' : '12px',
                          }}
                        >
                          <Typography variant="caption" sx={{ fontSize: '9px', fontWeight: 700, letterSpacing: '0.5px', opacity: 0.9 }}>
                            {hasToken ? 'TOKEN' : 'APPT'}
                          </Typography>
                          {tokenDisplay}
                        </Box>

                        {/* Patient Details */}
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
                              {patientName}
                            </Typography>
                            
                            {/* Appt ID & Patient ID Chips matching Doctor Portal */}
                            <Chip
                              label={apptIdDisplay}
                              size="small"
                              sx={{
                                height: '22px',
                                fontSize: '11px',
                                fontWeight: 600,
                                bgcolor: isDark ? 'rgba(79, 70, 229, 0.2)' : '#EEF2FF',
                                color: isDark ? '#A5B4FC' : '#4F46E5',
                              }}
                            />
                            <Chip
                              label={patientIdDisplay}
                              size="small"
                              sx={{
                                height: '22px',
                                fontSize: '11px',
                                fontWeight: 600,
                                bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#F1F5F9',
                                color: theme.palette.text.secondary,
                              }}
                            />

                            {(pAge || pGender) && (
                              <Chip
                                label={`${pAge} ${pGender ? '• ' + pGender : ''}`}
                                size="small"
                                sx={{
                                  height: '22px',
                                  fontSize: '11px',
                                  bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : '#F8FAFC',
                                  color: theme.palette.text.secondary,
                                  border: `1px solid ${theme.palette.divider}`,
                                }}
                              />
                            )}
                          </Box>

                          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 0.75, color: theme.palette.text.secondary, fontSize: '12px' }}>
                            <span>🕒 {item.time || '10:00 AM'}</span>
                            {pPhone && <span>📞 {pPhone}</span>}
                            {item.addnotes && <span>📋 <em>"{item.addnotes}"</em></span>}
                            {item.doctor?.fullName && <span>👨‍⚕️ <em>Dr. {item.doctor.fullName}</em></span>}
                          </Box>
                        </Box>
                      </Box>

                      {/* Right: Status & Quick Action Buttons */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Chip
                          label={item.recordStatus || 'Scheduled'}
                          size="small"
                          sx={{
                            bgcolor: statusStyle.bg,
                            color: statusStyle.text,
                            border: `1px solid ${statusStyle.border}`,
                            fontWeight: 700,
                            fontSize: '12px',
                            height: '28px',
                            px: 1,
                          }}
                        />

                        {/* WhatsApp Notification */}
                        {isWaiting && (
                          <Tooltip title="Send WhatsApp Queue Alert">
                            <IconButton
                              color="success"
                              size="small"
                              onClick={() => sendWhatsAppReminder(item)}
                              sx={{
                                border: `1px solid ${isDark ? '#065F46' : '#DCFCE7'}`,
                                bgcolor: isDark ? 'rgba(34, 197, 94, 0.15)' : '#F0FDF4',
                                '&:hover': { bgcolor: isDark ? 'rgba(34, 197, 94, 0.25)' : '#DCFCE7' },
                              }}
                            >
                              <WhatsAppIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* More Menu */}
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, item)}
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '8px',
                            color: theme.palette.text.primary,
                          }}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Card>
                );
              })}
            </Stack>
          )}
        </Box>
      </Card>

      {/* Action Popover Menu - View Profile Only */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
            minWidth: 150,
          },
        }}
      >
        <MenuItem
          onClick={() => handleViewPatientProfile(activeAppointment)}
          sx={{ py: 1.2, px: 2, display: 'flex', alignItems: 'center', gap: 1.2 }}
        >
          <PersonIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            View Profile
          </Typography>
        </MenuItem>
      </Menu>

      {/* Add Appointment Modal */}
      <FormDialog open={openAddModal} onClose={() => setOpenAddModal(false)}>
        <AddAppointment
          isQueue={true}
          defaultScheduleType="token"
          onClose={() => setOpenAddModal(false)}
          onSave={() => {
            fetchData();
            setOpenAddModal(false);
          }}
          onPatientAdded={fetchData}
        />
      </FormDialog>
    </Box>
  );
};

export default OPDQueueContainer;
